/**
 * Google Sheets Member Sync Component
 * Handles individual member sync operations
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNotifications } from '../common/NotificationSystem';
import { googleSheetsService } from '../../services/googleSheetsService';
import { memberService } from '../../services/memberService';
import { useRBAC } from '../../hooks/useRBAC';
import ProfessionalLoader from '../common/ProfessionalLoader';
import './GoogleSheetsMemberSync.css';

const GoogleSheetsMemberSync = () => {
  const { showSuccess, showError } = useNotifications();
  const { canManageIntegrations } = useRBAC();
  
  // State management
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [syncStatus, setSyncStatus] = useState({});

  // Load members
  const loadMembers = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await memberService.getMembers({
        ordering: '-created_at',
        page_size: 100
      });
      
      if (response.success) {
        setMembers(response.data || []);
      } else {
        showError(response.message || 'Failed to load members', 'Error');
      }
    } catch (error) {
      showError('Failed to load members', 'Error');
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  // Load members on component mount
  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  // Handle member selection
  const handleMemberSelect = (memberId, isSelected) => {
    setSelectedMembers(prev => {
      if (isSelected) {
        return [...prev, memberId];
      } else {
        return prev.filter(id => id !== memberId);
      }
    });
  };

  // Handle select all
  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedMembers(members.map(member => member.id));
    } else {
      setSelectedMembers([]);
    }
  };

  // Handle individual member sync
  const handleMemberSync = async (memberId) => {
    setIsProcessing(true);
    try {
      const response = await googleSheetsService.syncMemberToSheets(memberId);
      
      if (response.success) {
        showSuccess('Member synced to Google Sheets successfully', 'Success');
        setSyncStatus(prev => ({
          ...prev,
          [memberId]: 'synced'
        }));
      } else {
        showError(response.message || 'Failed to sync member', 'Error');
        setSyncStatus(prev => ({
          ...prev,
          [memberId]: 'failed'
        }));
      }
    } catch (error) {
      showError('Failed to sync member', 'Error');
      setSyncStatus(prev => ({
        ...prev,
        [memberId]: 'failed'
      }));
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle bulk sync
  const handleBulkSync = async () => {
    if (selectedMembers.length === 0) return;

    setIsProcessing(true);
    try {
      const response = await googleSheetsService.bulkSyncToSheets({
        member_ids: selectedMembers,
        sync_type: 'manual'
      });
      
      if (response.success) {
        showSuccess(`Successfully synced ${selectedMembers.length} members to Google Sheets`, 'Success');
        setSelectedMembers([]);
        // Update sync status for all selected members
        const newSyncStatus = {};
        selectedMembers.forEach(memberId => {
          newSyncStatus[memberId] = 'synced';
        });
        setSyncStatus(prev => ({ ...prev, ...newSyncStatus }));
      } else {
        showError(response.message || 'Failed to sync members', 'Error');
      }
    } catch (error) {
      showError('Failed to sync members', 'Error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Filter members based on search term
  const filteredMembers = members.filter(member =>
    member.organization_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.president_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.president_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.member_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="google-sheets-member-sync">
        <div className="loading-container">
          <ProfessionalLoader size="large" variant="default" />
        </div>
      </div>
    );
  }

  return (
    <div className="google-sheets-member-sync">
      {/* Header */}
      <div className="sync-header">
        <div className="header-content">
          <h2>🔄 Member Sync</h2>
          <p>Sync individual members or bulk sync to Google Sheets</p>
        </div>
        <div className="header-actions">
          <button
            className="btn btn-primary"
            onClick={handleBulkSync}
            disabled={selectedMembers.length === 0 || isProcessing || !canManageIntegrations}
          >
            {isProcessing ? 'Syncing...' : `Sync ${selectedMembers.length} Members`}
          </button>
        </div>
      </div>

      {/* Search and Selection */}
      <div className="sync-controls">
        <div className="search-section">
          <input
            type="text"
            className="search-input"
            placeholder="Search members by name, email, or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="selection-section">
          <label className="select-all-label">
            <input
              type="checkbox"
              checked={selectedMembers.length === filteredMembers.length && filteredMembers.length > 0}
              onChange={(e) => handleSelectAll(e.target.checked)}
            />
            Select All ({filteredMembers.length} members)
          </label>
          
          {selectedMembers.length > 0 && (
            <span className="selected-count">
              {selectedMembers.length} selected
            </span>
          )}
        </div>
      </div>

      {/* Members List */}
      <div className="members-list">
        {filteredMembers.length > 0 ? (
          <div className="members-table-container">
            <table className="members-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={selectedMembers.length === filteredMembers.length && filteredMembers.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </th>
                  <th>Member ID</th>
                  <th>Organization</th>
                  <th>Contact Person</th>
                  <th>Email</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMembers.map(member => (
                  <tr key={member.id} className="member-row">
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedMembers.includes(member.id)}
                        onChange={(e) => handleMemberSelect(member.id, e.target.checked)}
                      />
                    </td>
                    <td>
                      <span className="member-id">{member.member_id}</span>
                    </td>
                    <td>
                      <div className="org-info">
                        <div className="org-name">{member.organization_name}</div>
                        {member.is_test_society && (
                          <span className="test-badge">Test</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="contact-info">
                        <div className="contact-name">{member.primary_contact_name || member.president_name || 'N/A'}</div>
                        <div className="contact-role">
                          {member.contact_person_display || 'Contact'}
                        </div>
                      </div>
                    </td>
                    <td>
                      <a href={`mailto:${member.primary_contact_email || member.president_email}`} className="email-link">
                        {member.primary_contact_email || member.president_email || 'N/A'}
                      </a>
                    </td>
                    <td>
                      <div className="location-info">
                        <div className="city-state">{member.city}, {member.state}</div>
                        <div className="postcode">{member.postcode}</div>
                      </div>
                    </td>
                    <td>
                      <div className="status-info">
                        <span className={`status-badge ${member.membership_status}`}>
                          {member.membership_status}
                        </span>
                        <div className="active-status">
                          {member.is_active ? '✅ Active' : '❌ Inactive'}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => handleMemberSync(member.id)}
                          disabled={isProcessing || !canManageIntegrations}
                        >
                          {isProcessing ? 'Syncing...' : 'Sync'}
                        </button>
                        
                        {syncStatus[member.id] && (
                          <span className={`sync-status ${syncStatus[member.id]}`}>
                            {syncStatus[member.id] === 'synced' ? '✅' : '❌'}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">👥</div>
            <h3>No Members Found</h3>
            <p>No members match your search criteria.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoogleSheetsMemberSync;
